import { Box, Button, Divider, makeStyles, Tooltip, withStyles } from '@material-ui/core';
import classNames from 'classnames';
import React, { useCallback, useContext } from 'react';
import { Column, IndexRange, InfiniteLoader, Table, TableRowProps } from 'react-virtualized';
import { useTranslation } from 'react-i18next';
import { formatMonthForDisplay } from 'utils/date';
import { formatTons } from 'utils/weight';
import { colors } from 'theme';
import { useLatestClosureDate } from 'utils/closureHooks';
import { AppContext } from 'context';
import { ConditionedStockByClientByMonth } from './types';

type ClientStockListProps = {
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  list: ConditionedStockByClientByMonth[];
  loadNextPage: (params: IndexRange) => Promise<any>;
};

const useStyles = makeStyles(theme => ({
  tableContainer: {
    padding: 15,
  },
  tableContainerReactVirtualized: {
    padding: 15,
  },
  table: {
    minWidth: '20rem',
    '& *': {
      fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    },
  },
  headerCell: {
    padding: '11px 22px',
    borderTop: 'none',
    borderBottom: 'none',
    '&:not(:last-child)': {
      borderRight: `1px solid ${colors.greyLighten20}`,
    },
  },
  dataRow: {
    height: 35,
    borderBottom: `1px solid ${colors.greyLighten20}`,

    '& .ReactVirtualized__Table__rowColumn': {
      '&:not(:first-of-type)': {
        borderLeft: `1px solid ${colors.greyLighten20}`,
      },

      lineHeight: '35px',
      padding: '0 16px',
      margin: 0,
    },

    '&.ReactVirtualized__Table__headerRow': {
      fontSize: '16px',
      textTransform: 'initial',
      borderBottom: 'none',

      '& .ReactVirtualized__Table__headerColumn': {
        '&:not(:first-of-type)': {
          borderLeft: `1px solid ${colors.greyLighten20}`,
        },

        lineHeight: '16px',
        margin: 0,
        padding: '0 16px',
      },
    },
  },
  tableDivider: {
    width: '100%',
    height: 1,
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  tableDividerLeft: {
    width: 80,
    marginLeft: 0,
    marginRight: 12,
  },
  tableDividerRight: {
    width: 'auto',
    flexGrow: 1,
    marginLeft: 12,
    marginRight: 0,
  },
  dividerClosure: {
    backgroundColor: theme.palette.primary.main,
  },
  dividerNoClosure: {
    backgroundColor: theme.palette.warning.main,
  },
  sectionHeader: {
    textTransform: 'none',
    color: theme.palette.primary.main,
  },
  sectionHeaderNoClosure: {
    textTransform: 'none',
    color: theme.palette.warning.main,
  },
}));

const ClosureTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    fontSize: '12px',
    color: theme.palette.text.primary,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: theme.palette.warning.main,
    borderRadius: '4px',
    width: '255px',
    height: '64px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  arrow: {
    color: theme.palette.common.white,
    '&:before': {
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: theme.palette.warning.main,
    },
  },
}))(Tooltip);

export const ClientStockList: React.FC<ClientStockListProps> = ({
  hasNextPage,
  isNextPageLoading,
  list,
  loadNextPage,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isRowLoaded = ({ index }: { index: number }) => {
    return index < list.length;
  };

  const loadMoreRows = (params: IndexRange) =>
    isNextPageLoading || !hasNextPage ? Promise.resolve() : loadNextPage(params);

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();

  const [latestClosureDate] = useLatestClosureDate(sortingCenter);

  const isDateAfterClosure = useCallback(
    (month: string, year: string): boolean => {
      const date = new Date(Number(year), Number(month), 0);
      if (!latestClosureDate) return true;
      return date > latestClosureDate;
    },
    [latestClosureDate],
  );

  return (
    <InfiniteLoader isRowLoaded={isRowLoaded} loadMoreRows={loadMoreRows} rowCount={list.length + 1}>
      {({ onRowsRendered, registerChild }) => (
        <Table
          className={classes.tableContainerReactVirtualized}
          headerClassName={classes.headerCell}
          rowClassName={classes.dataRow}
          width={1000}
          height={600}
          headerHeight={35}
          ref={registerChild}
          rowCount={list.length + 1}
          rowGetter={({ index }): Partial<ConditionedStockByClientByMonth> =>
            isRowLoaded({ index }) ? list[index] : {}
          }
          onRowsRendered={onRowsRendered}
          overscanRowCount={5}
          rowHeight={({ index }) => {
            if (index === 0 || (isRowLoaded({ index }) && list[index - 1].month !== list[index].month)) {
              return 70;
            }

            return 35;
          }}
          rowRenderer={({ className, columns, index, key, rowData, style }: TableRowProps) => {
            if (!isRowLoaded({ index })) {
              return (
                <div role="row" className={className} key={key} style={style}>
                  {isNextPageLoading && 'Loading'}
                </div>
              );
            }

            if (index === 0 || list[index - 1].month !== rowData.month) {
              return (
                <div
                  role="row"
                  key={key}
                  style={{
                    ...style,
                    display: 'flex',
                    width: '100%',
                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <Box display="flex" fontWeight={600} className={className}>
                    <Divider
                      className={classNames(
                        classes.tableDivider,
                        isDateAfterClosure(rowData.month, rowData.year)
                          ? classes.dividerNoClosure
                          : classes.dividerClosure,
                        classes.tableDividerLeft,
                      )}
                    />
                    {isDateAfterClosure(rowData.month, rowData.year) ? (
                      <ClosureTooltip title={<>{t('stocks.client.noClosure')}</>} placement="right" arrow>
                        <Button className={classes.sectionHeaderNoClosure}>
                          {formatMonthForDisplay(rowData.month, rowData.year)} {rowData.year}
                        </Button>
                      </ClosureTooltip>
                    ) : (
                      <Button className={classes.sectionHeader}>
                        {formatMonthForDisplay(rowData.month, rowData.year)} {rowData.year}
                      </Button>
                    )}

                    <Divider
                      className={classNames(
                        classes.tableDivider,
                        isDateAfterClosure(rowData.month, rowData.year)
                          ? classes.dividerNoClosure
                          : classes.dividerClosure,
                        classes.tableDividerRight,
                      )}
                    />
                  </Box>
                  <div className={className}>{columns}</div>
                </div>
              );
            }

            return (
              <div role="row" className={className} key={key} style={style}>
                {columns}
              </div>
            );
          }}
        >
          <Column width={200} label="Client" dataKey="client" />
          <Column width={200} label="Matériaux" dataKey="material" />
          <Column
            width={185}
            label="Tonnage"
            dataKey="weight"
            headerStyle={{ textAlign: 'end' }}
            style={{ textAlign: 'end' }}
            cellRenderer={({ cellData }) => formatTons(cellData)}
          />
        </Table>
      )}
    </InfiniteLoader>
  );
};
